import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { Editor } from "@toast-ui/react-editor";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "@/firebase/config";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";
import "@toast-ui/editor/dist/toastui-editor.css";

// Custom CSS to override Toast UI Editor styles
const editorStyles = `
  .toastui-editor-defaultUI {
    border: none !important;
  }
  
  .toastui-editor-defaultUI-toolbar {
    border-bottom: 1px solid rgb(227, 233, 226) !important;
    background-color: rgb(253, 252, 248) !important;
  }
  
  .toastui-editor-toolbar-icons {
    border: 1px solid rgb(227, 233, 226) !important;
    background-color: white !important;
    color: rgb(47, 78, 59) !important;
  }
  
  .toastui-editor-toolbar-icons:hover {
    background-color: rgb(47, 78, 59, 0.05) !important;
    border-color: rgb(47, 78, 59) !important;
  }
  
  .toastui-editor-defaultUI .ProseMirror {
    color: rgb(47, 78, 59) !important;
  }
  
  .toastui-editor-defaultUI .toastui-editor-md-container,
  .toastui-editor-defaultUI .toastui-editor-ww-container {
    background-color: white !important;
  }
`;

interface EditorWrapperProps {
  initialValue?: string;
  onChange?: (html: string) => void;
  height?: string;
}

const EditorWrapper = ({
  initialValue = "",
  onChange,
  height = "600px",
}: EditorWrapperProps) => {
  const editorRef = useRef<Editor>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const { toast } = useToast();
  const lastContentRef = useRef<string>(initialValue);
  const isInitializedRef = useRef(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Debounced onChange handler to prevent excessive state updates
  const handleChange = useCallback(() => {
    const instance = editorRef.current?.getInstance();
    if (instance && onChange && isInitializedRef.current) {
      const html = instance.getHTML();
      // Only call onChange if content actually changed
      if (html !== lastContentRef.current) {
        lastContentRef.current = html;

        // Clear previous timeout
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        // Debounce the onChange call by 150ms
        timeoutRef.current = setTimeout(() => {
          onChange(html);
        }, 150);
      }
    }
  }, [onChange]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Memoize toolbar items to prevent recreation on every render
  const toolbarItems = useMemo(
    () => [
      ["heading", "bold", "italic", "strike"],
      ["hr", "quote"],
      ["ul", "ol", "task"],
      ["table", "image", "link"],
      ["code", "codeblock"],
    ],
    []
  );

  // Function to handle image upload to Firebase Storage
  const handleImageUpload = useCallback(
    async (blob: Blob, callback: (url: string, text: string) => void) => {
      setIsUploadingImage(true);

      try {
        // Validate file type
        const allowedTypes = [
          "image/jpeg",
          "image/jpg",
          "image/png",
          "image/gif",
          "image/webp",
        ];
        if (!allowedTypes.includes(blob.type)) {
          throw new Error(
            "Please upload a valid image file (JPEG, PNG, GIF, or WebP)"
          );
        }

        // Validate file size (max 5MB)
        const maxSize = 5 * 1024 * 1024; // 5MB
        if (blob.size > maxSize) {
          const sizeMB = (blob.size / (1024 * 1024)).toFixed(2);
          throw new Error(`Image size (${sizeMB}MB) exceeds the 5MB limit`);
        }

        // Create a unique filename with proper extension
        const extension = blob.type.split("/")[1] || "jpg";
        const timestamp = Date.now();
        const randomId = Math.random().toString(36).substring(7);
        const fileName = `editor-images/${timestamp}-${randomId}.${extension}`;
        const storageRef = ref(storage, fileName);

        // Upload the image to Firebase Storage
        console.log("Uploading image to Firebase Storage...", {
          fileName,
          size: `${(blob.size / 1024).toFixed(2)}KB`,
          type: blob.type,
        });

        const uploadTask = await uploadBytes(storageRef, blob);

        // Get the download URL
        const downloadUrl = await getDownloadURL(uploadTask.ref);
        console.log("Image uploaded successfully:", downloadUrl);

        // Show success message
        toast({
          title: "Image Uploaded",
          description:
            "Image has been uploaded successfully and inserted into the editor",
        });

        // Call the callback with the download URL
        callback(downloadUrl, "Image");
      } catch (error) {
        console.error("Error uploading image:", error);

        // Show error toast
        toast({
          variant: "destructive",
          title: "Upload Failed",
          description:
            error instanceof Error ? error.message : "Failed to upload image",
        });

        // Don't fallback to base64 - just don't insert the image
        // This ensures all images go through Firebase Storage
      } finally {
        setIsUploadingImage(false);
      }
    },
    [toast]
  );

  useEffect(() => {
    // Add custom styles to document
    const styleElement = document.createElement("style");
    styleElement.textContent = editorStyles;
    document.head.appendChild(styleElement);

    const instance = editorRef.current?.getInstance();
    if (instance) {
      // Set initial content only once
      if (!isInitializedRef.current) {
        instance.setHTML(initialValue);
        lastContentRef.current = initialValue;
        isInitializedRef.current = true;
      }

      // Add image upload hook
      instance.addHook("addImageBlobHook", handleImageUpload);
    }

    // Cleanup
    return () => {
      styleElement.remove();
    };
  }, [initialValue, handleImageUpload]);

  return (
    <div className="w-full overflow-hidden relative">
      {isUploadingImage && (
        <div className="absolute top-2 right-2 z-10 bg-white/90 rounded-md px-3 py-2 flex items-center gap-2 shadow-sm border">
          <Loader2 className="w-4 h-4 animate-spin text-forest-green" />
          <span className="text-sm text-forest-green">Uploading image...</span>
        </div>
      )}
      <Editor
        key="editor-instance"
        ref={editorRef}
        initialValue={initialValue}
        previewStyle="vertical"
        height={height}
        initialEditType="markdown"
        useCommandShortcut={true}
        onChange={handleChange}
        toolbarItems={toolbarItems}
        hooks={{
          addImageBlobHook: handleImageUpload,
        }}
      />
    </div>
  );
};

export default EditorWrapper;
